import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LangueEnum } from '../constantes/langue.enum';
import { AdresseMouvement } from 'src/app/api/model/adresse-mouvement.model';
import { LangueService } from '@services/langue/langue.service';

@Pipe({
  name: 'adresse',
  standalone: false,
})
export class AdressePipe implements PipeTransform {
  isFrancais: boolean = true;
  isCanada: boolean = true;
  trail: string = '';
  avantAppartement: string = '';
  private readonly espaceInsecable = '\u00A0';
  private readonly regCodePostalByGroup: RegExp = /([^-\s]{3})(\s?)([^-\s]{3})/;
  private readonly maskCodePostal: string = `$1${this.espaceInsecable}$3`;
  private readonly appartementFr: string = 'appartement';
  private readonly appartementEn: string = 'Apartment';

  private readonly langueService = inject(LangueService);
  readonly translateService = inject(TranslateService);

  constructor() {}

  transform(coordonnees: AdresseMouvement | Partial<AdresseMouvement> | null): string {
    if (!coordonnees) {
      return '';
    }
    this.isCanada = coordonnees.codePays === 'CA' || !coordonnees.codePays;
    this.isFrancais = this.langueService.getCurrentLanguage() === LangueEnum.FR;
    this.trail = this.isFrancais ? '' : ',';
    this.avantAppartement = this.isFrancais ? ', ' : '\n';
    if (this.isCanada) {
      return this.formatAdresse(coordonnees);
    }
    return this.formatAdresseHorsCanada(coordonnees);
  }

  private formatAdresse(coordonnees: AdresseMouvement | Partial<AdresseMouvement>): string {
    const numeroCiviqueNomrue = this.cleanNumeroCiviqueNomRueBancaire(coordonnees);
    const municipaliteSubdivisionCodePostal =
      this.cleanMunicipaliteSubdivisionCodePostal(coordonnees);
    return [numeroCiviqueNomrue, municipaliteSubdivisionCodePostal].filter(Boolean).join('\n');
  }

  private formatAdresseHorsCanada(
    coordonnees: AdresseMouvement | Partial<AdresseMouvement>,
  ): string {
    const numeroCiviqueNomrue = this.cleanNumeroCiviqueNomRueBancaire(coordonnees);
    const municipaliteSubdivisionCodePostal = coordonnees.nomMunicipalite
      ? this.cleanMunicipaliteSubdivisionCodePostal(coordonnees)
      : coordonnees.texteComplementHorsCanada;
    const pays = this.cleanPaysHorsCanada(coordonnees);
    return [numeroCiviqueNomrue, municipaliteSubdivisionCodePostal, pays]
      .filter(Boolean)
      .join('\n');
  }

  private cleanNumeroCiviqueNomRueBancaire(
    coordonnees: AdresseMouvement | Partial<AdresseMouvement>,
  ): string {
    let adresse = '';
    if (coordonnees.numeroCivique || coordonnees.nomRue) {
      adresse = coordonnees.numeroCivique
        ? (coordonnees.numeroCivique ?? '') + this.getSeparation() + (coordonnees.nomRue ?? '')
        : (coordonnees.nomRue ?? '');
    }
    adresse += coordonnees.numeroUnite
      ? this.avantAppartement + this.cleanUnite(coordonnees.numeroUnite)
      : '';
    return adresse;
  }

  private getSeparation(): string {
    return this.isFrancais ? ', ' : ' ';
  }

  private cleanMunicipaliteSubdivisionCodePostal(
    coordonnees: AdresseMouvement | Partial<AdresseMouvement>,
  ): string {
    const nomMunicipalite = coordonnees.nomMunicipalite ?? '';
    const codeSubdivision = this.cleanProvince(coordonnees.codeSubdivisionPays ?? '');
    const libelleSubdivision = coordonnees.libelleSubdivision ?? '';
    const codePostal = this.cleanPostal(coordonnees.codePostal ?? '');

    if (this.isFrancais) {
      let subdivision = '';
      if (libelleSubdivision) {
        subdivision = ` (${libelleSubdivision})`;
      } else if (codeSubdivision) {
        subdivision = ` ${codeSubdivision}`;
      }
      return `${nomMunicipalite}${subdivision}  ${codePostal}`.trim();
    } else {
      return `${nomMunicipalite} ${codeSubdivision}  ${codePostal}`.trim();
    }
  }

  private cleanProvince(value: string): string {
    if (!value) {
      return '';
    }
    value = value.replaceAll(/[^a-zA-Z]/g, '');
    if (value.length < 2) {
      return '';
    }
    return this.isFrancais && this.isCanada
      ? this.getDescriptionCodeSubdivisionPays(value.slice(-2))
      : this.formaterProvenceSelonLangue(value.slice(-2));
  }

  formaterProvenceSelonLangue(value: string): string {
    return this.isFrancais ? '(' + value + ')' : value;
  }

  private cleanPaysHorsCanada(
    coordonnees: AdresseMouvement | Partial<AdresseMouvement>,
  ): string | undefined {
    return coordonnees.libellePays ?? coordonnees.codePays;
  }

  private getDescriptionCodeSubdivisionPays(value: string): string {
    let province = this.translateService.instant(`donneeReference.provincesAffichage.${value}`);
    value = this.isFrancais ? '(' + value + ')' : value;
    return province ?? value;
  }

  private cleanUnite(numeroUnite: string): string {
    return this.isFrancais
      ? this.appartementFr + this.espaceInsecable + numeroUnite
      : this.appartementEn + this.espaceInsecable + numeroUnite;
  }

  private cleanPostal(value: string): string {
    if (!value) {
      return '';
    }
    return this.isCanada ? value.replace(this.regCodePostalByGroup, this.maskCodePostal) : value;
  }
}
